import {FC, useContext} from "react";
import {Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {IAdminUsersLessonsResponseEntry, IEntity, ILocalizationEntity} from "@buildwitt/shapeshift-types";
import {useApi} from "@buildwitt/shapeshift-identity";
import {ConfirmDeleteEntityDialog} from "@buildwitt/shapeshift-common/v2/admin";
import {AdminTable, AdminTableProps} from "@/components/common/table";
import {GRID_CHECKBOX_SELECTION_COL_DEF} from "@mui/x-data-grid-pro";
import { PageBaseContext } from "@/contexts/pageBaseContext";
import { convertSecondsToHHMMSS } from "@/utils";
import { IBaseSearchDTO } from "@/components/common/table/AdminTable";

const headCells = (locales?: ILocalizationEntity[], ownedCohortTitle?: string, extendedAccess?: boolean): AdminTableProps<"lesson", IAdminUsersLessonsResponseEntry>["columns"] => ([
	{
		...GRID_CHECKBOX_SELECTION_COL_DEF,
		type: "string",
		hideable: false
	},
	{
		field: "title",
		type: "string",
		flex: 1,
		headerName: "Title",
		minWidth: 240
	},
	{
		field: "videoDuration",
		type: "number",
		headerAlign: "left",
		align: "left",
		flex: 1,
		headerName: "Duration",
		valueGetter: (e) => e.value ? convertSecondsToHHMMSS(e.value) : ""
	},
	{
		field: "updatedAt",
		type: "date",
		flex: 1,
		headerName: "Last Modified",
		valueGetter: (e) => e.value ? new Date(e.value).toLocaleDateString() : ""
	},
	{
		field: "publishedAt",
		type: "date",
		flex: 1,
		headerName: "Published",
		valueGetter: (e) => e.value ? new Date(e.value).toLocaleDateString() : ""
	},
	{
		field: "languages",
		type: "string",
		filterable: false,
		flex: 1,
		headerName: "Supported Languages",
		valueGetter: ({value}: {value: IAdminUsersLessonsResponseEntry["languages"]}) => value?.map(e => locales?.find(x => x.code === e)?.name)?.join(", ")
	},
	...(extendedAccess ? [{
		field: "authorCohort",
		type: "string" as const,
		flex: 1,
		filterable: false,
		sortable: false,
		headerName: "Shared By",
		valueGetter: ({value}: {value: IAdminUsersLessonsResponseEntry["authorCohort"]}) => (value?.title ?? "") === ownedCohortTitle ? "" : value?.title
	}] : [])
]);

type IProps = {
	extendedAccess?: boolean
} & Partial<AdminTableProps<"lesson", IAdminUsersLessonsResponseEntry>>;

export const AdminLessonsTable: FC<IProps> = ({extendedAccess, ...baseProps}) => {
	const navigate = useNavigate();
	const [locales] = useApi<ILocalizationEntity[]>("/api/settings/i18n");
	const [{ownedCohort}] = useContext(PageBaseContext)

	return (
		<AdminTable<"lesson", IAdminUsersLessonsResponseEntry, IBaseSearchDTO<IEntity> & {extendedAccess?: boolean}>
			url="/api/strapi-plugin-shapeshift-lms/cohort/lesson"
			columns={headCells(locales.data, typeof ownedCohort === "object" ? ownedCohort?.title : undefined, extendedAccess)}
			searchDTOverride={() => extendedAccess ? ({extendedAccess: true}) : undefined}
			onEditAction={e => navigate(e.id.toString())}
			enableDelete
			type="lesson"
			filterSettingsPersistenceStorageKey="lessonFilterSettings"
			componentOverrides={{
				deleteDialog: deleteProps => <ConfirmDeleteEntityDialog
					{...deleteProps}
					componentOverrides={{
						content: (deleteProps?.entity?.enrollmentCount && deleteProps.entity?.enrollmentCount >= 1) ? <Typography variant="body1">You will be making an assigned lesson unavailable.</Typography> : undefined
					}}
				/>
			}}
			{...baseProps}
		/>
	);
}
